import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, errMsg } from '../organization/finance/shared';
import Icon from '../../components/dashboard/DashboardIcons';
import { relativeTime, dateOnly, fullDateTime, PresenceDot } from './presence';
import { normalizeWhatsAppNumber } from '../../lib/whatsapp';

const sauth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const PLAN_LABELS = { free: 'مجانية', basic: 'أساسية', premium: 'مميزة', enterprise: 'مؤسسات', venue_trial: 'تجريبية (ملاعب)', coach_pro: 'المدرب الاحترافي' };
const STATUS_LABELS = { active: 'نشطة', inactive: 'معطلة', pending: 'معلقة', suspended: 'موقوفة' };
const SUB_STATUS_LABELS = { active: 'اشتراك نشط', trial: 'تجريبي', expired: 'منتهي', cancelled: 'ملغي' };
const ROLE_SHORT = { admin: 'مدير', manager: 'مشرف', coach: 'مدرب', analyst: 'محلل', parent: 'ولي أمر', venue_owner: 'مالك ملعب' };
const METHOD_LABELS = { bank_transfer: 'تحويل بنكي', cash: 'نقدي', card: 'بطاقة', other: 'أخرى' };
const nf = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('en-US'));
const money = (v) => (v === null || v === undefined ? '—' : Math.round(Number(v)).toLocaleString('en-US'));

const Stat = ({ label, value, testId }) => (
  <div className="rounded-xl bg-slate-50 px-3 py-2.5" data-testid={testId}>
    <div className="text-[10px] font-bold text-slate-400">{label}</div>
    <div className="mt-0.5 text-lg font-extrabold tabular-nums leading-none text-slate-900">{value}</div>
  </div>
);

const Section = ({ title, action, children, testId }) => (
  <section className="px-5 py-4" data-testid={testId}>
    <div className="mb-2.5 flex items-center justify-between gap-2">
      <h4 className="text-xs font-bold text-slate-500">{title}</h4>
      {action}
    </div>
    {children}
  </section>
);

export default function ClubProfileDrawer({ clubId, onClose, onWhatsApp, onChanged, notify }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmRenew, setConfirmRenew] = useState(false);

  const load = useCallback(async () => {
    if (!clubId) return;
    setData(null); setErr(null);
    try {
      const r = await axios.get(`${API}/saas/admin/clubs/${clubId}/profile`, sauth());
      setData(r.data);
    } catch (e) { setErr(errMsg(e, 'تعذّر جلب ملف المنشأة')); }
  }, [clubId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const renew30 = async () => {
    setBusy(true);
    try {
      const cur = data?.subscription?.end_date ? new Date(data.subscription.end_date) : null;
      const base = cur && cur > new Date() ? cur : new Date();
      const next = new Date(base.getTime() + 30 * 86400000);
      await axios.put(`${API}/saas/admin/clubs/${clubId}/subscription`, {
        status: 'active', end_date: next.toISOString().slice(0, 10),
      }, sauth());
      setConfirmRenew(false);
      notify?.('تم تمديد الاشتراك 30 يوماً');
      await load();
      onChanged?.();
    } catch (e) { notify?.(errMsg(e, 'فشل تمديد الاشتراك')); }
    setBusy(false);
  };

  if (!clubId) return null;
  const club = data?.club || {};
  const sub = data?.subscription || {};
  const act = data?.activity || {};
  const st = data?.stats || {};
  const days = sub.days;

  return (
    <div className="fixed inset-y-0 inset-x-0 z-[60] flex" data-testid="club-profile-drawer">
      <button type="button" aria-label="إغلاق" onClick={onClose} className="flex-1 bg-slate-900/50 backdrop-blur-sm" data-testid="club-profile-backdrop" />
      <aside className="flex h-full w-full max-w-xl flex-col overflow-y-auto bg-white shadow-2xl" style={{ animation: 'ccSlideIn .3s cubic-bezier(.2,.7,.3,1) both' }}>
        {/* ترويسة */}
        <header className="relative shrink-0 overflow-hidden bg-[#0f1726] px-5 py-5 text-white">
          <span className="pointer-events-none absolute -left-16 -top-20 h-48 w-48 rounded-full bg-teal-500/20 blur-3xl" />
          <div className="relative flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="truncate text-lg font-bold" data-testid="club-profile-name">{club.name || '...'}</h3>
                {act.is_online && <PresenceDot online testId="club-profile-online-dot" />}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px]">
                <span className="rounded-full bg-white/10 px-2 py-0.5 font-semibold text-white/70" dir="ltr">/{club.slug}</span>
                <span className="rounded-full bg-white/10 px-2 py-0.5 font-semibold text-white/70">{STATUS_LABELS[club.status] || club.status}</span>
                <span className="rounded-full bg-teal-400/20 px-2 py-0.5 font-bold text-teal-200">{PLAN_LABELS[sub.plan] || sub.plan || 'بلا باقة'}</span>
                {club.city && <span className="text-white/50">{club.city}</span>}
              </div>
              <p className="mt-2 text-[11px] text-white/45">
                منضمة {relativeTime(club.created_at) || '—'} · {act.last_login ? `آخر دخول ${relativeTime(act.last_login)} (${act.last_login_by})` : 'لم يدخل أي مستخدم بعد'}
              </p>
            </div>
            <button onClick={onClose} className="shrink-0 rounded-lg p-1.5 text-white/60 transition-colors hover:bg-white/10 hover:text-white" data-testid="club-profile-close">✕</button>
          </div>
          <div className="relative mt-4 flex flex-wrap gap-2">
            <button onClick={() => onWhatsApp?.({ name: club.name, phone: club.phone, plan_name: PLAN_LABELS[sub.plan] || sub.plan, expiry_date: sub.end_date, amount: sub.amount })}
              disabled={!normalizeWhatsAppNumber(club.phone)} data-testid="club-profile-whatsapp-btn"
              className="flex items-center gap-1.5 rounded-xl bg-emerald-500 px-3 py-2 text-xs font-bold text-white transition-colors hover:bg-emerald-600 disabled:bg-white/10 disabled:text-white/40">
              <Icon name="whatsapp" className="h-3.5 w-3.5" /> رسالة واتساب
            </button>
            <button onClick={() => setConfirmRenew(true)} data-testid="club-profile-renew-btn"
              className="flex items-center gap-1.5 rounded-xl bg-white/10 px-3 py-2 text-xs font-bold text-white transition-colors hover:bg-white/20">
              <Icon name="refresh" className="h-3.5 w-3.5" /> تمديد 30 يوم
            </button>
            <a href={`/club/${club.slug}`} target="_blank" rel="noreferrer" data-testid="club-profile-public-link"
              className="flex items-center gap-1.5 rounded-xl bg-white/10 px-3 py-2 text-xs font-bold text-white transition-colors hover:bg-white/20">
              <Icon name="flag" className="h-3.5 w-3.5" /> الصفحة العامة
            </a>
          </div>
        </header>

        {err && <div className="m-5 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="club-profile-error">{err}</div>}
        {!data && !err && <div className="space-y-3 p-5">{[1, 2, 3].map((i) => <div key={i} className="h-20 animate-pulse rounded-xl bg-slate-100" />)}</div>}

        {data && (
          <div className="divide-y divide-slate-100">
            {/* الاشتراك */}
            <Section title="الاشتراك" testId="club-profile-subscription">
              <div className="rounded-2xl border border-slate-200 p-4">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="text-sm font-bold text-slate-800">{PLAN_LABELS[sub.plan] || sub.plan || 'بلا باقة'}</div>
                    <div className="mt-0.5 text-[11px] text-slate-400">{SUB_STATUS_LABELS[sub.status] || sub.status || '—'} · ينتهي {dateOnly(sub.end_date)}</div>
                  </div>
                  <span className={`rounded-full px-3 py-1 text-xs font-bold tabular-nums ${days === null || days === undefined ? 'bg-slate-100 text-slate-500' : days < 0 ? 'bg-red-50 text-red-600' : days <= 14 ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`} data-testid="club-profile-days-badge">
                    {days === null || days === undefined ? 'بلا تاريخ' : days < 0 ? `منتهي منذ ${Math.abs(days)} يوم` : `${days} يوم متبقي`}
                  </span>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  <Stat label="إجمالي المدفوع" value={`${money(data.payments_total)}`} testId="club-profile-paid-total" />
                  <Stat label="عدد الدفعات" value={nf(data.payments?.length)} />
                  <Stat label="قيمة الاشتراك" value={sub.amount ? money(sub.amount) : '—'} />
                </div>
              </div>
            </Section>

            {/* الأرقام */}
            <Section title="أرقام المنشأة" testId="club-profile-stats">
              <div className="grid grid-cols-4 gap-2">
                <Stat label="لاعب" value={nf(st.players)} testId="club-profile-stat-players" />
                <Stat label="مستخدم" value={nf(st.users)} />
                <Stat label="فريق" value={nf(st.teams)} />
                <Stat label="فئة" value={nf(st.categories)} />
                <Stat label="مباراة" value={nf(st.matches)} />
                <Stat label="موظف" value={nf(st.employees)} />
                <Stat label="باقة" value={nf(st.packages)} />
                <Stat label="إيراد داخلي" value={money(st.internal_revenue)} />
              </div>
            </Section>

            {/* تاريخ المدفوعات */}
            <Section title={`تاريخ المدفوعات للمنصة (${nf(data.payments?.length)})`} testId="club-profile-payments">
              {(data.payments || []).length === 0 ? (
                <p className="rounded-xl bg-slate-50 px-4 py-6 text-center text-xs text-slate-400">لا توجد دفعات مسجلة لهذه المنشأة — سجّلها من تبويب «المحاسبة المالية»</p>
              ) : (
                <ol className="space-y-2.5">
                  {data.payments.map((p) => (
                    <li key={p.id} className="rounded-xl border border-slate-200 px-3.5 py-3" data-testid="club-profile-payment-row">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-extrabold tabular-nums text-emerald-700">{money(p.amount)} ريال</span>
                        <span className="text-[11px] font-semibold text-slate-500">{dateOnly(p.payment_date)}</span>
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-slate-400">
                        <span className="rounded bg-slate-100 px-1.5 py-0.5 font-semibold text-slate-500">{PLAN_LABELS[p.plan] || p.plan}</span>
                        <span>{METHOD_LABELS[p.payment_method] || p.payment_method}</span>
                        <span>· {p.billing_period_months} شهر</span>
                        <span>· {dateOnly(p.period_start)} ← {dateOnly(p.period_end)}</span>
                      </div>
                      {p.notes && <p className="mt-1.5 text-[11px] text-slate-500">{p.notes}</p>}
                    </li>
                  ))}
                </ol>
              )}
            </Section>

            {/* المستخدمون */}
            <Section title={`المستخدمون (${nf(st.users)})`} testId="club-profile-users">
              <ul className="divide-y divide-slate-50">
                {(data.users || []).length === 0 ? <p className="py-4 text-center text-xs text-slate-400">لا يوجد مستخدمون</p> : data.users.map((u, i) => (
                  <li key={i} className="flex items-center gap-2.5 py-2.5" data-testid="club-profile-user-row">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-xs font-bold text-slate-600">{(u.name || '؟').slice(0, 1)}</span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-bold text-slate-800">{u.name}</div>
                      <div className="truncate text-[11px] text-slate-400" dir="ltr">{u.username}</div>
                    </div>
                    <div className="shrink-0 text-end">
                      <div className="text-[11px] font-bold text-slate-500">{ROLE_SHORT[u.role] || u.role}</div>
                      <div className="text-[10px] text-slate-400" title={fullDateTime(u.last_login)}>{u.is_online ? 'متصل الآن' : relativeTime(u.last_login) || 'لم يدخل'}</div>
                    </div>
                    <PresenceDot online={u.is_online} />
                  </li>
                ))}
              </ul>
            </Section>

            {/* التواصل */}
            <Section title="التواصل" testId="club-profile-contact">
              <div className="space-y-1.5 text-sm">
                <div className="flex items-center justify-between"><span className="text-xs text-slate-400">الجوال</span><span dir="ltr" className="font-semibold text-slate-700">{club.phone || '—'}</span></div>
                <div className="flex items-center justify-between"><span className="text-xs text-slate-400">البريد</span><span dir="ltr" className="font-semibold text-slate-700">{club.email || '—'}</span></div>
                <div className="flex items-center justify-between"><span className="text-xs text-slate-400">مسؤول التواصل</span><span className="font-semibold text-slate-700">{club.contact_person || '—'}</span></div>
              </div>
            </Section>
          </div>
        )}

        {confirmRenew && (
          <div className="sticky bottom-0 border-t border-slate-200 bg-white p-4" data-testid="club-profile-renew-confirm">
            <p className="text-sm font-semibold text-slate-700">تمديد اشتراك «{club.name}» 30 يوماً وتفعيله؟</p>
            <p className="mt-1 text-[11px] text-slate-400">يُحسب من تاريخ الانتهاء الحالي إن كان مستقبلاً، وإلا من اليوم.</p>
            <div className="mt-3 flex justify-end gap-2">
              <button onClick={() => setConfirmRenew(false)} className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600">إلغاء</button>
              <button onClick={renew30} disabled={busy} className="rounded-xl bg-[#16233b] px-4 py-2 text-sm font-bold text-white disabled:opacity-50" data-testid="club-profile-renew-confirm-btn">
                {busy ? 'جارٍ…' : 'تأكيد التمديد'}
              </button>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}
